'use client'

import type { ActionMenuItem } from '@/components/actions-menu'
import { sendConsultationEmail, printConsultation } from '@/lib/api/consultations/consultation-api'
import { printExamOrder, sendExamOrderEmail } from '@/lib/api/exams/exam-api'
import { printOrder, sendOrderEmail } from '@/lib/api/orders/order-api'
import { useState } from 'react'
import { MdDownload, MdEmail, MdOpenInNew, MdPrint } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

/** Os três tipos de atendimento que o prontuário lista. */
export type RecordKind = 'exam' | 'consultation' | 'order'

interface UseRecordActionsOptions {
    kind: RecordKind
    id: number
    /** Sem e-mail cadastrado, a ação de enviar aparece desabilitada e explica. */
    clientEmail?: string | null
}

/**
 * Ações rápidas de um atendimento, para o dropdown das listas do prontuário e para
 * o fim do pedido de exames.
 *
 * **Só leitura e envio.** Alterar status, gerar cobrança, cancelar e excluir ficam
 * na tela de detalhe, onde há confirmação e contexto: um "Excluir" a um clique numa
 * lista de prontuário é acidente esperando acontecer, e quem está no meio do
 * atendimento não quer decidir isso.
 *
 * As três variantes moram juntas porque a diferença entre elas é só qual função de
 * API chamar — o resto (estado de carregando, mensagem de erro, e-mail ausente)
 * é idêntico, e estava a caminho de ser copiado em três telas.
 */
export function useRecordActions({ kind, id, clientEmail }: UseRecordActionsOptions) {
    const navigate = useNavigate()
    const [printing, setPrinting] = useState(false)
    const [sending, setSending] = useState(false)

    const detailPath =
        kind === 'exam'
            ? `/bookings/exams/${id}`
            : kind === 'consultation'
            ? `/bookings/consultations/${id}`
            : `/bookings/orders/${id}`

    const label =
        kind === 'exam' ? 'pedido' : kind === 'consultation' ? 'consulta' : 'procedimento'

    const handlePdf = async (delivery: 'print' | 'download') => {
        setPrinting(true)
        try {
            if (kind === 'exam') await printExamOrder(id, delivery)
            else if (kind === 'consultation') await printConsultation(id, delivery)
            else await printOrder(id, delivery)

            // No modo impressão quem dá o retorno é a janela do navegador; avisar
            // "gerado com sucesso" ali seria um segundo aviso do mesmo fato.
            if (delivery === 'download') toast.success('PDF gerado')
        } catch {
            toast.error('Erro ao gerar o PDF')
        } finally {
            setPrinting(false)
        }
    }

    const handleSendEmail = async () => {
        setSending(true)
        try {
            const response =
                kind === 'exam'
                    ? await sendExamOrderEmail(id)
                    : kind === 'consultation'
                    ? await sendConsultationEmail(id)
                    : await sendOrderEmail(id)

            if (response.success) toast.success(`Enviamos o ${label} por e-mail`)
            else toast.error(response.error || 'Erro ao enviar por e-mail')
        } catch {
            toast.error('Erro ao enviar por e-mail')
        } finally {
            setSending(false)
        }
    }

    const hasEmail = !!clientEmail?.trim()

    const actions: ActionMenuItem[] = [
        {
            key: 'detail',
            label: 'Ver detalhes',
            icon: <MdOpenInNew />,
            onClick: () => navigate(detailPath),
        },
        {
            key: 'print',
            label: 'Imprimir',
            icon: <MdPrint />,
            onClick: () => handlePdf('print'),
            loading: printing,
        },
        {
            key: 'download',
            label: 'Baixar PDF',
            icon: <MdDownload />,
            onClick: () => handlePdf('download'),
            loading: printing,
        },
        {
            key: 'email',
            label: 'Enviar por e-mail',
            icon: <MdEmail />,
            onClick: handleSendEmail,
            loading: sending,
            disabled: !hasEmail,
            // Desabilitado sem explicação vira "não funciona": o operador precisa
            // saber que o caminho é cadastrar o e-mail do paciente.
            title: hasEmail ? undefined : 'Paciente sem e-mail cadastrado',
        },
    ]

    return { actions, printing, sending, handlePdf, handleSendEmail, hasEmail }
}
